import {
  CalendarOutlined,
  FilterFilled,
  SearchOutlined,
} from '@ant-design/icons';
import { Button, Checkbox, DatePicker, Input } from 'antd';
import type { FilterDropdownProps } from 'antd/lib/table/interface';
import moment from 'moment';
import React, { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { APIKeyDetails } from '../../../constants/types';
import { STATUS_OPTIONS } from '../../../helpers/apiKey';

const { RangePicker } = DatePicker;

const FilterDropdownContainer = styled.div<{ width: number }>`
  padding: 8px;
  width: ${({ width }) => `${width}px`};

  .date-picker {
    width: 100%;
    margin-bottom: 8px;
  }
`;

const FilterSearchContainer = styled.div`
  margin-bottom: 8px;
`;

const FullWidthInput = styled(Input)`
  width: 100%;

  &.ant-input-affix-wrapper {
    height: 32px;
    min-height: 32px;
    padding-top: 0;
    padding-bottom: 0;
  }

  &.ant-input-affix-wrapper .ant-input-prefix {
    display: inline-flex;
    align-items: center;
  }

  &.ant-input-affix-wrapper > input.ant-input {
    height: 30px;
    line-height: 30px;
    padding-top: 0;
    padding-bottom: 0;
  }
`;

const FilterActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FilterResetText = styled.span<{ disabled: boolean }>`
  color: ${({ disabled, theme }) =>
    disabled ? '#bfbfbf' : theme.palette.primary.main};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  user-select: none;
`;

const FilterOkButton = styled(Button)`
  min-width: auto !important;
  width: auto !important;
  padding: 0 12px !important;
`;

const StatusCheckboxGroup = styled(Checkbox.Group)`
  display: flex;
  flex-direction: column;

  .ant-checkbox-wrapper + .ant-checkbox-wrapper {
    margin-left: 0;
  }
`;

const StatusCheckbox = styled(Checkbox)`
  margin: 4px 0;
`;

const StatusFooter = styled.div`
  margin-top: 8px;
`;

export const FilterIcon = styled.span<{ filtered: boolean }>`
  color: ${({ filtered, theme }) =>
    filtered ? theme.palette.primary.main : 'inherit'};
  display: inline-flex;
  font-size: 14px;
`;

export const SearchFilterIcon = ({ filtered }: { filtered: boolean }) => (
  <FilterIcon filtered={filtered}>
    <SearchOutlined />
  </FilterIcon>
);

export const CalendarFilterIcon = ({ filtered }: { filtered: boolean }) => (
  <FilterIcon filtered={filtered}>
    <CalendarOutlined />
  </FilterIcon>
);

export const StatusFilterIcon = ({ filtered }: { filtered: boolean }) => (
  <FilterIcon filtered={filtered}>
    <FilterFilled />
  </FilterIcon>
);

const FilterActions = ({
  hasValue,
  onReset,
  onOk,
}: {
  hasValue: boolean;
  onReset: () => void;
  onOk: () => void;
}) => (
  <FilterActionsContainer className="ant-table-filter-dropdown-btns border-none">
    <FilterResetText
      role="button"
      tabIndex={0}
      disabled={!hasValue}
      onClick={() => {
        if (!hasValue) return;
        onReset();
      }}
      onKeyDown={event => {
        if ((event.key === 'Enter' || event.key === ' ') && hasValue) {
          onReset();
        }
      }}
    >
      <FormattedMessage id="Global.reset" />
    </FilterResetText>
    <FilterOkButton type="primary" size="small" onClick={onOk}>
      <FormattedMessage id="Global.ok" />
    </FilterOkButton>
  </FilterActionsContainer>
);

export const TextFilterDropdown = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
  placeholder,
}: FilterDropdownProps & { placeholder: string }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const input = containerRef.current?.querySelector('input');
    input?.focus();
  }, []);

  return (
    <FilterDropdownContainer width={300} ref={containerRef}>
      <FilterSearchContainer className="ant-table-filter-dropdown-search">
        <FullWidthInput
          size="small"
          prefix={<SearchOutlined />}
          placeholder={placeholder}
          value={selectedKeys.toString() || ''}
          onChange={e =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => confirm?.()}
        />
      </FilterSearchContainer>
      <FilterActions
        hasValue={!!selectedKeys.toString()}
        onReset={() => clearFilters?.()}
        onOk={() => confirm?.()}
      />
    </FilterDropdownContainer>
  );
};

const RangeFooterNowLink = styled.a`
  color: ${({ theme }) => theme.palette.text.purpleDarkened3};
  font-weight: 500;
`;

export const DateRangeFilterDropdown = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
}: FilterDropdownProps) => {
  const handleNow = () => {
    const now = moment();
    setSelectedKeys([now, now] as unknown as React.Key[]);
  };
  return (
    <FilterDropdownContainer width={340}>
      <RangePicker
        showTime
        dropdownClassName="apikey-datepicker-dropdown"
        value={
          selectedKeys as any as [moment.Moment, moment.Moment] | undefined
        }
        onChange={dates => setSelectedKeys(dates ? (dates as any) : [])}
        className="date-picker"
        renderExtraFooter={() => (
          <RangeFooterNowLink
            role="button"
            tabIndex={0}
            onClick={handleNow}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') handleNow();
            }}
          >
            <FormattedMessage id="Global.now" defaultMessage="Now" />
          </RangeFooterNowLink>
        )}
      />
      <FilterActions
        hasValue={selectedKeys.length > 0}
        onReset={() => clearFilters?.()}
        onOk={() => confirm()}
      />
    </FilterDropdownContainer>
  );
};

export const SingleDateFilterDropdown = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
}: FilterDropdownProps) => {
  const value =
    (selectedKeys[0] as unknown as moment.Moment | undefined) ?? undefined;
  return (
    <FilterDropdownContainer width={300}>
      <DatePicker
        showTime
        value={value}
        dropdownClassName="apikey-datepicker-dropdown"
        onChange={date =>
          setSelectedKeys(date ? ([date] as unknown as React.Key[]) : [])
        }
        className="date-picker"
      />
      <FilterActions
        hasValue={selectedKeys.length > 0}
        onReset={() => clearFilters?.()}
        onOk={() => confirm()}
      />
    </FilterDropdownContainer>
  );
};

export const StatusFilterDropdown = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
}: FilterDropdownProps) => (
  <FilterDropdownContainer width={220}>
    <StatusCheckboxGroup
      value={selectedKeys as APIKeyDetails['status'][]}
      onChange={vals => setSelectedKeys(vals as APIKeyDetails['status'][])}
    >
      {STATUS_OPTIONS.map(opt => (
        <StatusCheckbox key={opt.value} value={opt.value}>
          <FormattedMessage id={opt.label} />
        </StatusCheckbox>
      ))}
    </StatusCheckboxGroup>
    <StatusFooter>
      <FilterActions
        hasValue={selectedKeys.length > 0}
        onReset={() => clearFilters?.()}
        onOk={() => confirm()}
      />
    </StatusFooter>
  </FilterDropdownContainer>
);
